import { useState } from "react";

type ExportColumn<T> = {
  header: string;
  value: (row: T) => string | number | boolean | null | undefined;
};

type ExportButtonProps<T> = {
  label: string;
  loadingLabel?: string;
  filename: string;
  rows: T[];
  columns: ExportColumn<T>[];
  className?: string;
};

export default function ExportButton<T>({
  label,
  loadingLabel,
  filename,
  rows,
  columns,
  className,
}: Readonly<ExportButtonProps<T>>) {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const XLSX = await import("xlsx");
      const headers = columns.map((column) => column.header);
      const data = rows.map((row) => {
        return headers.reduce<Record<string, string | number | boolean>>((acc, header, index) => {
          const value = columns[index].value(row);
          acc[header] = value ?? "";
          return acc;
        }, {});
      });

      const worksheet = XLSX.utils.json_to_sheet(data, { header: headers });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

      const safeName = filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`;
      XLSX.writeFile(workbook, safeName);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleExport}
      disabled={isExporting || rows.length === 0}
      className={className}
    >
      {isExporting ? (loadingLabel || label) : label}
    </button>
  );
}
